/**
 * Робота з датами. Назви місяців і днів беремо з Intl за локаллю організації,
 * а не зі списків у коді — тому продукт одразу говорить мовою користувача.
 */
export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export function parseIso(s) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
}
export const todayIso = () => iso(new Date());
export const daysInMonth = (year, month0) => new Date(year, month0 + 1, 0).getDate();
export const monthKey = (year, month0) => `${year}-${String(month0 + 1).padStart(2, '0')}`;
export function monthDates(year, month0) {
    const n = daysInMonth(year, month0);
    return Array.from({ length: n }, (_, i) => iso(new Date(year, month0, i + 1)));
}
/**
 * Зсув дати. Відповідь кешуємо: підрахунок нарядів питає сусідній день для
 * кожної клітинки місяця, і на таблиці в кілька тисяч клітинок це були тисячі
 * зайвих `new Date`. Ключ — рядок дати, тож кеш не старіє.
 */
const shifted = new Map();
export function addDays(s, n) {
    const key = `${s}|${n}`;
    const hit = shifted.get(key);
    if (hit)
        return hit;
    const d = parseIso(s);
    d.setDate(d.getDate() + n);
    const out = iso(d);
    shifted.set(key, out);
    return out;
}
export function rangeDates(from, to) {
    const out = [];
    for (let d = from; d <= to; d = addDays(d, 1))
        out.push(d);
    return out;
}
/**
 * День тижня 0..6, де 0 — неділя. Кешуємо: у таблиці це питають на кожну
 * клітинку, а дата — незмінний рядок, тож відповідь ніколи не зміниться.
 */
const weekdays = new Map();
export const weekday = (s) => {
    const hit = weekdays.get(s);
    if (hit !== undefined)
        return hit;
    const w = parseIso(s).getDay();
    weekdays.set(s, w);
    return w;
};
/** Індекс у тижні з урахуванням початку тижня організації. */
export const weekIndex = (s, weekStart) => (weekday(s) - weekStart + 7) % 7;
export const isWeekend = (s) => {
    const w = weekday(s);
    return w === 0 || w === 6;
};
const cache = new Map();
function fmt(locale, opts) {
    const k = locale + JSON.stringify(opts);
    let f = cache.get(k);
    if (!f) {
        f = new Intl.DateTimeFormat(locale, opts);
        cache.set(k, f);
    }
    return f;
}
const LOCALES = { uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' };
export const intlLocale = (locale) => LOCALES[locale] ?? locale;
/** «Березень 2026» */
export function monthTitle(year, month0, locale = 'uk') {
    const s = fmt(intlLocale(locale), { month: 'long', year: 'numeric' }).format(new Date(year, month0, 1));
    return s.charAt(0).toUpperCase() + s.slice(1);
}
/** Назви місяців у називному: ['Січень', …]. */
export function monthNames(locale = 'uk') {
    const f = fmt(intlLocale(locale), { month: 'long' });
    return Array.from({ length: 12 }, (_, i) => {
        const s = f.format(new Date(2000, i, 1));
        return s.charAt(0).toUpperCase() + s.slice(1);
    });
}
/** «15 березня 2026 року» — для документів. */
export function longDate(d, locale = 'uk') {
    const date = parseIso(d);
    const base = fmt(intlLocale(locale), { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
    return locale === 'uk' ? base.replace(/\s*р\.?$/, ' року') : base;
}
/** «15 березня» */
export function dayMonth(d, locale = 'uk') {
    return fmt(intlLocale(locale), { day: 'numeric', month: 'long' }).format(parseIso(d));
}
/** «15.03» */
export const shortDate = (d) => d.slice(8) + '.' + d.slice(5, 7);
/**
 * Назви днів тижня теж кешуємо. `Intl` форматує повільно, а таблиця просить
 * підпис для кожної клітинки — на місяці це десятки тисяч однакових викликів.
 */
const names = new Map();
function weekdayName(d, locale, style) {
    const key = `${style}|${locale}|${d}`;
    const hit = names.get(key);
    if (hit !== undefined)
        return hit;
    const raw = fmt(intlLocale(locale), { weekday: style }).format(parseIso(d));
    const out = style === 'short' ? raw.replace('.', '') : raw;
    names.set(key, out);
    return out;
}
/** «пн», «вт» … */
export function weekdayShort(d, locale = 'uk') {
    return weekdayName(d, locale, 'short');
}
/** «понеділок» */
export function weekdayLong(d, locale = 'uk') {
    return weekdayName(d, locale, 'long');
}
/** Заголовки колонок тижневого шаблону з урахуванням початку тижня. */
export function weekdayHeaders(weekStart, locale = 'uk') {
    const f = fmt(intlLocale(locale), { weekday: 'short' });
    // 2024-01-07 — неділя
    return Array.from({ length: 7 }, (_, i) => f.format(new Date(2024, 0, 7 + ((i + weekStart) % 7))).replace('.', ''));
}
/** Excel-серійний номер дати (епоха 1899-12-30). */
export const excelSerial = (d) => {
    const [y, m, dd] = d.split('-').map(Number);
    return Math.round((Date.UTC(y, m - 1, dd) - Date.UTC(1899, 11, 30)) / 864e5);
};
/**
 * Період, набраний коротко: «01-15», «09.08-17.08», «16», «16.08».
 *
 * Так його диктують і так його пишуть у переписці, тому вводити дві повні
 * дати з календаря — зайва робота. Число без місяця належить місяцю, який
 * відкритий у таблиці; кінець раніше за початок міняється з ним місцями.
 *
 * Повертає `null`, якщо розібрати не вдалося: краще нічого, ніж навмання
 * обраний період у документі.
 */
export function parsePeriod(text, year, month0) {
    const t = (text ?? '').replace(/[–—]/g, '-').replace(/\s+/g, '');
    if (!t)
        return null;
    const day = (d, m) => {
        // Місяць поза 1..12 — це помилка набору, а не інший рік: лишаємо поточний.
        const mm = m !== undefined && Number(m) >= 1 && Number(m) <= 12 ? Number(m) - 1 : month0;
        const last = daysInMonth(year, mm);
        const dd = Math.min(Math.max(Number(d), 1), last);
        return `${year}-${String(mm + 1).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    };
    const order = (a, b) => a <= b ? { from: a, to: b } : { from: b, to: a };
    let m = /^(\d{1,2})$/.exec(t);
    if (m)
        return order(day(m[1]), day(m[1]));
    m = /^(\d{1,2})\.(\d{1,2})$/.exec(t);
    if (m)
        return order(day(m[1], m[2]), day(m[1], m[2]));
    m = /^(\d{1,2})-(\d{1,2})$/.exec(t);
    if (m)
        return order(day(m[1]), day(m[2]));
    m = /^(\d{1,2})\.(\d{1,2})-(\d{1,2})\.(\d{1,2})$/.exec(t);
    if (m)
        return order(day(m[1], m[2]), day(m[3], m[4]));
    m = /^(\d{1,2})-(\d{1,2})\.(\d{1,2})$/.exec(t);
    if (m)
        return order(day(m[1]), day(m[2], m[3]));
    return null;
}
//# sourceMappingURL=dates.js.map